/**
 * メニュー関連のカスタムフック
 * - useSidebarMenus: Redux の生メニューデータをサイドバー構造に組み立てて返す
 * - useTabsByCode:   指定コードの親メニュー配下のタブ一覧を返す
 * - useTabsByUrl:    現在URLが属するタブ一覧と親メニュー情報を返す
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import type { RootState } from "../../app/store";
import { buildSidebarMenus, findMenuByUrl } from "./buildMenus";
import type { MenuItem, SidebarMenus } from "./menuTypes";

/**
 * サイドバーメニュー（上部・下部 × 親子）を取得するフック
 * Redux store の menu.items を監視し、権限/翻訳を適用して返す
 */
export function useSidebarMenus(): SidebarMenus {
    const { t } = useTranslation("common");
    const { user } = useAuth();
    const dbMenus = useSelector((state: RootState) => state.menu.items);

    const permissionLevel = user?.permissionLevel ?? 0;

    return useMemo(
        () => buildSidebarMenus(dbMenus, t, permissionLevel),
        [dbMenus, t, permissionLevel],
    );
}

/**
 * 指定 code の親メニュー配下のタブ一覧を返す
 * 例: useTabsByCode("tenant") → テナント配下の全タブ
 */
export function useTabsByCode(parentCode: string): MenuItem[] {
    const menus = useSidebarMenus();
    return useMemo(() => {
        const parent = menus.top.find((m) => m.code === parentCode);
        return parent?.children ?? [];
    }, [menus, parentCode]);
}

/**
 * 現在のURLが属する親メニューのタブ一覧と親情報を返す
 * タブページ用: ページ側で parent code を意識せず使える
 */
export function useCurrentTabs(): {
    parent: MenuItem | null;
    tabs: MenuItem[];
    activeTab: MenuItem | null;
} {
    const menus = useSidebarMenus();
    const location = useLocation();

    return useMemo(() => {
        const found = findMenuByUrl(menus, location.pathname);
        if (!found) return { parent: null, tabs: [], activeTab: null };

        // 一致したのがタブ自体なら、親とその子一覧を返す
        if (found.parent) {
            return {
                parent: found.parent,
                tabs: found.parent.children,
                activeTab: found.item,
            };
        }

        // 一致したのが親メニューなら、自分の子一覧を返し activeTab は先頭
        return {
            parent: found.item,
            tabs: found.item.children,
            activeTab: found.item.children[0] ?? null,
        };
    }, [menus, location.pathname]);
}
